import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Star, TrendingUp, Heart, Download, Filter, RotateCcw,
  MessageSquare, Building2, User, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackDashboard = ({ user, onLogout }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [therapists, setTherapists] = useState([]);
  
  // Filters
  const [filterProperty, setFilterProperty] = useState('');
  const [filterTherapist, setFilterTherapist] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFeedbacks();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProperty, filterTherapist, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [propsRes, therapistsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers }),
        axios.get(`${API}/therapists`, { headers })
      ]);
      
      setProperties(propsRes.data.filter(p => p.status !== 'archived'));
      setTherapists(therapistsRes.data.filter(t => t.status !== 'archived'));
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterProperty && filterProperty !== 'all') params.append('property_id', filterProperty);
      if (filterTherapist && filterTherapist !== 'all') params.append('therapist_id', filterTherapist);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/feedbacks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbacks(response.data);
    } catch (error) {
      toast.error('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterProperty && filterProperty !== 'all') params.append('property_id', filterProperty);
      if (filterTherapist && filterTherapist !== 'all') params.append('therapist_id', filterTherapist);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/feedbacks/analytics?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const handleAppreciate = async (feedbackId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/feedbacks/${feedbackId}/appreciate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Therapist appreciated!');
      fetchFeedbacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to appreciate');
    }
  };

  const resetFilters = () => {
    setFilterProperty('');
    setFilterTherapist('');
    setDateFrom('');
    setDateTo('');
  };

  const exportToExcel = () => {
    if (feedbacks.length === 0) {
      toast.error('No feedback data to export');
      return;
    }
    
    // Create CSV content
    const headers = ['Date', 'Property', 'Guest', 'Therapist', 'Cleanliness', 'Service', 'Therapist Rating', 'Overall', 'Comments'];
    const rows = feedbacks.map(f => [
      new Date(f.created_at).toLocaleDateString(),
      f.property_id,
      f.guest_name,
      f.therapist_name || '-',
      f.cleanliness_rating,
      f.service_rating,
      f.therapist_rating,
      f.overall_rating,
      f.feedback_text || ''
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Report downloaded');
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star 
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream">
      {/* Header */}
      <div className="glass border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">Feedback Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Total Feedbacks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{analytics.total_feedbacks}</p>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription>Cleanliness Avg</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{analytics.avg_cleanliness}</p>
                  {renderStars(Math.round(analytics.avg_cleanliness))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription>Service Avg</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{analytics.avg_service}</p>
                  {renderStars(Math.round(analytics.avg_service))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription>Therapist Avg</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{analytics.avg_therapist}</p>
                  {renderStars(Math.round(analytics.avg_therapist))}
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader className="pb-2">
                <CardDescription>Overall Avg</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">{analytics.avg_overall}</p>
                  {renderStars(Math.round(analytics.avg_overall))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Therapist Rankings */}
        {analytics?.therapist_rankings?.length > 0 && (
          <div className="glass rounded-2xl p-6">
            <h3 className="text-lg font-serif text-foreground mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" /> Top Therapists
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.therapist_rankings.slice(0, 4).map((t, index) => (
                <div key={t.therapist_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <span className={`text-2xl font-bold ${
                    index === 0 ? 'text-amber-500' : 
                    index === 1 ? 'text-gray-400' :
                    index === 2 ? 'text-amber-700' : 'text-muted-foreground'
                  }`}>
                    #{index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{t.therapist_name}</p>
                    <div className="flex items-center gap-1">
                      {renderStars(Math.round(t.avg_rating))}
                      <span className="text-xs text-muted-foreground ml-1">
                        ({t.feedback_count})
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass rounded-2xl p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="w-[180px]" data-testid="filter-property">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.hotel_name}>{p.hotel_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterTherapist} onValueChange={setFilterTherapist}>
              <SelectTrigger className="w-[180px]" data-testid="filter-therapist">
                <SelectValue placeholder="All Therapists" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Therapists</SelectItem>
                {therapists.map(t => (
                  <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[150px]"
              placeholder="From"
            />
            
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[150px]"
              placeholder="To"
            />
            
            <Button variant="outline" size="icon" onClick={resetFilters} title="Reset">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Feedbacks List */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-serif text-foreground mb-4">
            Feedback Entries ({feedbacks.length})
          </h3>
          
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : feedbacks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No feedback entries found
            </div>
          ) : (
            <div className="space-y-4">
              {feedbacks.map(feedback => (
                <div key={feedback.id} className="border border-border/30 rounded-xl p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{feedback.guest_name}</span>
                        <span className="text-xs text-muted-foreground">
                          • {new Date(feedback.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Building2 className="w-3 h-3" /> {feedback.property_id}
                        </span>
                        {feedback.therapist_name && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <User className="w-3 h-3" /> {feedback.therapist_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Cleanliness</p>
                          {renderStars(feedback.cleanliness_rating)}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Service</p>
                          {renderStars(feedback.service_rating)}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Therapist</p>
                          {renderStars(feedback.therapist_rating)}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Overall</p>
                          {renderStars(feedback.overall_rating)}
                        </div>
                      </div>
                      
                      {feedback.feedback_text && (
                        <p className="text-sm text-muted-foreground italic">
                          "{feedback.feedback_text}"
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {feedback.appreciated ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          Appreciated
                        </Badge>
                      ) : feedback.overall_rating >= 4 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAppreciate(feedback.id)}
                          className="border-pink-300 hover:bg-pink-50"
                          data-testid={`appreciate-${feedback.id}`}
                        >
                          <Heart className="w-4 h-4 mr-1 text-pink-500" />
                          Appreciate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackDashboard;
